import { Component, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { provideNativeDateAdapter } from '@angular/material/core';
import { EventService, ApiEvent } from '../../../services/event.service';
import { UserService } from '../../../services/user.service';
import { GroupService } from '../../../services/group.service';
import { Group } from '../../../models/group';

const GRADE_OPTIONS = [
  { label: 'Grade 6', value: 6 },
  { label: 'Grade 7', value: 7 },
  { label: 'Grade 8', value: 8 },
  { label: 'Grade 9', value: 9 },
  { label: 'Grade 10', value: 10 },
  { label: 'Grade 11', value: 11 },
  { label: 'Grade 12', value: 12 },
];

/**
 * Converts a stored "HH:MM:SS" time into a 12-hour display format.
 * @param time - Time in "HH:MM:SS" format
 * @returns Formatted string like "2:30 PM"
 */
function timeToDisplay(time: string): string {
  const [h, m] = time.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 || 12;
  return `${hour}:${m.toString().padStart(2, '0')} ${period}`;
}

/**
 * Converts a user-entered 12-hour time string into the stored "HH:MM:SS" format.
 * An AM/PM meridiem is required — inputs without one are rejected as ambiguous.
 * @param display - Time like "2:30 PM", "2:30PM", or "2 PM"
 * @returns Time in "HH:MM:SS" format, or empty string if unrecognized
 */
function displayToTime(display: string): string {
  const trimmed = display.trim();

  const match = trimmed.match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)$/i);
  if (!match) return '';

  let hours = parseInt(match[1]);
  const minutes = match[2] ?? '00';
  const period = match[3].toUpperCase();
  if (hours < 1 || hours > 12 || parseInt(minutes) > 59) return '';
  if (period === 'PM' && hours !== 12) hours += 12;
  if (period === 'AM' && hours === 12) hours = 0;
  return `${hours.toString().padStart(2, '0')}:${minutes}:00`;
}

/** Builds list of 15-minute-increment time options */
function buildTimeOptions(): string[] {
  const options: string[] = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 15) {
      options.push(
        timeToDisplay(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:00`),
      );
    }
  }
  return options;
}

const TIME_OPTIONS = buildTimeOptions();

/**
 * Validator that flags an unparseable time. Empty values are considered valid
 * here so requiredness is handled separately by Validators.required.
 */
function validTime(control: AbstractControl): ValidationErrors | null {
  const value = (control.value ?? '').trim();
  if (!value) return null;
  return displayToTime(value) ? null : { invalidTime: true };
}

/** Parses a "YYYY-MM-DD" string into a local Date. */
function parseDate(day: string): Date | null {
  const m = day.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  return new Date(+m[1], +m[2] - 1, +m[3]);
}

/** Formats a Date into a "YYYY-MM-DD" string using local components. */
function formatDate(date: Date): string {
  const y = date.getFullYear();
  const mo = (date.getMonth() + 1).toString().padStart(2, '0');
  const d = date.getDate().toString().padStart(2, '0');
  return `${y}-${mo}-${d}`;
}

@Component({
  selector: 'app-event-builder',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatInputModule,
    MatFormFieldModule,
    MatSelectModule,
    MatIconModule,
    MatSlideToggleModule,
    MatDatepickerModule,
    MatAutocompleteModule,
  ],
  providers: [provideNativeDateAdapter()],
  templateUrl: './event-builder.html',
  styleUrl: './event-builder.css',
})
/** Dialog component for creating or editing a counselor/student event. */
export class EventBuilder implements OnInit {
  dialogRef = inject(MatDialogRef<EventBuilder>);
  eventService = inject(EventService);
  userService = inject(UserService);
  groupService = inject(GroupService);

  groups = signal<Group[]>([]);

  data = inject<ApiEvent | null>(MAT_DIALOG_DATA);

  /** True if the current user is a counselor (admin). */
  get isCounselor(): boolean {
    return this.userService.currentUser?.userType === 'faculty';
  }

  gradeOptions = GRADE_OPTIONS;
  timeOptions = TIME_OPTIONS;
  errorMessage = '';

  form = new FormGroup({
    title: new FormControl('', Validators.required),
    day: new FormControl<Date | null>(null, Validators.required),
    start_time: new FormControl('', [Validators.required, validTime]),
    end_time: new FormControl('', validTime),
    place: new FormControl(''),
    description: new FormControl(''),
    is_shared: new FormControl(false),
    grades: new FormControl<number[]>(GRADE_OPTIONS.map((g) => g.value)),
    groups: new FormControl<number[]>([]),
  });

  /** Populates the form with existing event data when opened in edit mode. */
  ngOnInit(): void {
    this.loadGroups();
    if (this.data) {
      this.form.patchValue({
        title: this.data.title,
        day: parseDate(this.data.day),
        start_time: timeToDisplay(this.data.start_time),
        end_time: this.data.end_time ? timeToDisplay(this.data.end_time) : '',
        place: this.data.place ?? '',
        description: this.data.description ?? '',
        is_shared: this.data.is_shared,
        grades: this.data.EventGrades?.map((g) => g.grade_level) ?? [],
      });
    }
  }

  /** True when the dialog was opened with an existing event (edit mode). */
  get isEditMode(): boolean {
    return !!this.data;
  }

  /** True when the is_shared toggle is enabled on the form. */
  get isShared(): boolean {
    return !!this.form.get('is_shared')?.value;
  }

  /** Filters the 15-minute time options against what the user has typed so far. */
  filterTimeOptions(value: string | null | undefined): string[] {
    const query = (value ?? '').trim().toLowerCase();
    if (!query) return this.timeOptions;
    return this.timeOptions.filter((t) =>
      t.toLowerCase().replace(/\s/g, '').includes(query.replace(/\s/g, '')),
    );
  }

  /** Submits the form, creating a new event or updating the existing one. */
  submit(): void {
    if (this.form.invalid) return;
    this.errorMessage = '';

    const v = this.form.value;
    const start_time = displayToTime(v.start_time!);

    if (!start_time) {
      this.errorMessage = 'Start time format not recognized. Try "10:30 AM".';
      return;
    }

    const payload = {
      title: v.title!,
      day: formatDate(v.day!),
      start_time,
      end_time: v.end_time ? displayToTime(v.end_time) || undefined : undefined,
      place: v.place || undefined,
      description: v.description || undefined,
      is_shared: v.is_shared ?? false,
      grades: v.grades ?? [],
      groups: v.groups ?? [],
    };

    const request$ = this.isEditMode
      ? this.eventService.updateEvent(this.data!.id, payload)
      : this.eventService.createEvent(payload);

    request$.subscribe({
      next: (event) => this.dialogRef.close({ action: 'saved', event }),
      error: (err) => {
        console.error('Event save failed:', err);
        this.errorMessage = 'Something went wrong. Please try again.';
      },
    });
  }

  /** Deletes the current event and closes the dialog. */
  delete(): void {
    if (!this.data) return;
    this.eventService.deleteEvent(this.data.id).subscribe({
      next: () => this.dialogRef.close({ action: 'deleted', eventId: this.data!.id }),
      error: (err) => {
        console.error('Event delete failed:', err);
        this.errorMessage = 'Something went wrong. Please try again.';
      },
    });
  }

  loadGroups() {
    this.groupService.getManagedGroups().subscribe({
      next: (groups) => {
        this.groups.set(groups);
      },
      error: (err) => {
        console.error(err);
      },
    });
  }
}
